import { useState } from "react"
import { useNavigate } from "react-router-dom"
import API from "../services/api"
import { getUser } from '../utils/storage'
import TeamSelector from "./TeamSelector"
import { useToast } from "../components/GlobalToast"

const ContestCard = ({ contest, matchId, myTeams, onJoin, teamIdFromUrl }) => {

  const navigate = useNavigate()
  const toast = useToast()
  const [showTeamSelector, setShowTeamSelector] = useState(false)

  // 🔎 Check if a specific team has already joined this contest
  const hasTeamJoined = (teamId) => (contest.joined_team_ids || []).includes(Number(teamId))

  // Check if the team from URL or only team has already joined
  const isJoined = teamIdFromUrl
    ? hasTeamJoined(teamIdFromUrl)
    : myTeams.length === 1
      ? hasTeamJoined(myTeams[0].team_id || myTeams[0].id)
      : myTeams.length > 0 && myTeams.every(t => hasTeamJoined(t.team_id || t.id)) // All teams joined

  const handleJoin = async (teamId) => {
    // ✅ Per-team duplicate check BEFORE calling API
    if (hasTeamJoined(teamId)) {
      return toast.warn("Yeh team pehle se joined hai! 🚫")
    }
    try {
      const user = getUser()
      await API.post("/contests/join", {
        contestId: contest.id,
        teamId: teamId,
        userId: user.id || 1
      })
      toast.success("Successfully joined contest! 🎉")
      navigate(`/my-teams?matchId=${matchId}`)
      if (onJoin) onJoin()
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || "Error joining contest."
      toast.error(errorMsg)
    }
  }

  const handleJoinClick = () => {
    if (isJoined) return
    if (teamIdFromUrl) {
      handleJoin(teamIdFromUrl)
    } else if (!myTeams || myTeams.length === 0) {
      navigate(`/create-team/${matchId}`)
    } else if (myTeams.length === 1) {
      handleJoin(myTeams[0].team_id || myTeams[0].id)
    } else {
      setShowTeamSelector(true)
    }
  }

  return (
    <div className="bg-theme-card rounded-theme p-4 mb-4 border border-theme-border shadow-theme-sm relative overflow-hidden group transition-all duration-300">

      {/* 🔝 Top Row: Title & Prize */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h2 className="text-lg font-bold text-theme-text leading-tight tracking-tight uppercase">
            {contest.title || "Mega Contest"}
          </h2>
          <p className="text-[10px] text-theme-text-sec mt-1 uppercase tracking-wider font-medium">Win upto ₹{Number(contest.first_prize).toLocaleString()}</p>
        </div>

        <div className="flex flex-col items-end">
          <span className="text-[9px] font-bold text-accent uppercase tracking-wider mb-1">Prize Pool</span>
          <p className="text-xl font-bold text-theme-text">₹{Number(contest.first_prize).toLocaleString()}</p>
        </div>
      </div>

      {/* 🚀 Middle: Join Button */}
      <button
        onClick={handleJoinClick}
        className={`w-full py-3 rounded-xl flex justify-center items-center gap-2 mb-4 transition-all relative overflow-hidden ${isJoined ? 'bg-theme-bg border border-theme-border opacity-70 cursor-not-allowed' : 'bg-accent text-white hover:brightness-105 active:scale-[0.98] shadow-sm'}`}
      >
        <span className="font-bold uppercase text-xs tracking-wider">
          {isJoined ? 'Joined' : 'Join'}
        </span>
        {!isJoined && (
          <span className="bg-white/20 px-2 py-0.5 rounded-lg text-sm font-bold">₹{contest.entry_fee}</span>
        )}
      </button>

      {/* Team Selection Popup */}
      {showTeamSelector && (
        <TeamSelector
          teams={myTeams}
          joinedTeamIds={contest.joined_team_ids || []}
          onSelect={(id) => {
            setShowTeamSelector(false)
            handleJoin(id)
          }}
          onClose={() => setShowTeamSelector(false)}
        />
      )}

      {/* 📊 Bottom: Progress & Stats */}
      <div className="flex justify-between items-center text-[10px] font-medium text-theme-text-sec tracking-wide bg-theme-bg/50 p-2.5 rounded-xl border border-theme-border/50">
        <div className="flex items-center gap-2">
          <span className="bg-theme-card border border-theme-border px-1.5 py-0.5 rounded text-[9px]">M</span>
          <span>{Number(contest.user_joined_count || 0)} / 10 Teams</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Progress Bar (Optional but nice for minimal UI) */}
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-accent transition-all duration-500" 
              style={{ width: `${Math.min(100, (Number(contest.joined_spots) / 100) * 100)}%` }}
            />
          </div>
          <span className="text-theme-text font-bold">{(Number(contest.joined_spots) + Number(contest.fake_joined || 0)).toLocaleString()} / {Number(contest.total_spots || 100).toLocaleString()} Spots</span>
        </div>
      </div>
    </div>
  )
}

export default ContestCard